// Het tabblad Lijst: categoriechips, de selects Toon en Sorteer, en het raster in twee secties.
// De keuze staat in de hash-query (#/?cat=…&toon=…&sort=…) zodat een gedeelde link dezelfde lijst toont.
import type { GuestGift, ListResponse } from '../shared/api-types.ts';
import { $, h } from './dom.ts';
import { giftCard } from './gift-card.ts';
import {
  DEFAULT_QUERY, applyQuery, categoriesOf, isDefaultQuery, listQueryToString, parseListQuery, withKnownCategory,
  type ListQuery,
} from './list-filter.ts';

let query: ListQuery = DEFAULT_QUERY;
let onQueryChange: () => void = () => {};

/** De link naar de lijst met de huidige keuze, voor het tabblad Lijst. */
export function listHref(): string {
  return `#/${listQueryToString(query)}`;
}

export function setQueryFromRoute(params: URLSearchParams): void {
  query = parseListQuery(params);
}

function setQuery(next: ListQuery): void {
  query = next;
  history.replaceState(null, '', listHref()); // geen hashchange: de pagina springt niet naar boven
  onQueryChange();
}

function chip(label: string, cat: string | null, active: boolean): HTMLButtonElement {
  const b = h('button', { type: 'button', class: 'fchip', 'aria-pressed': String(active) }, label);
  b.dataset.fk = `chip-${cat ?? ''}`;
  b.addEventListener('click', () => setQuery({ ...query, cat }));
  return b;
}

function renderChips(categories: string[]): void {
  const chips = $('chips');
  chips.hidden = categories.length === 0;
  chips.replaceChildren(
    chip('Alle', null, query.cat === null),
    ...categories.map((c) => chip(c, c, query.cat === c)));
}

function renderSection(kind: 'pots' | 'gifts', gifts: GuestGift[]): void {
  $(`${kind}Section`).hidden = gifts.length === 0;
  $(`${kind}Count`).textContent = String(gifts.length);
  $(kind).replaceChildren(...gifts.map(giftCard));
}

function renderEmpty(total: number, shown: number): void {
  const box = $('emptyState');
  box.hidden = shown > 0;
  $('emptyText').textContent = total === 0
    ? 'Er staat nog niets op de lijst. Kom binnenkort nog eens kijken.'
    : 'Niets gevonden voor deze keuze.';
  $('showAll').hidden = total === 0 || isDefaultQuery(query);
}

function syncSelects(): void {
  ($('toon') as HTMLSelectElement).value = query.show;
  ($('sort') as HTMLSelectElement).value = query.sort;
}

function focusKey(): string | undefined {
  const el = document.activeElement;
  return el instanceof HTMLElement ? el.dataset.fk : undefined;
}

export function renderListView(list: ListResponse | null): void {
  if (!list) return;
  const fk = focusKey();
  const categories = categoriesOf(list.gifts);
  query = withKnownCategory(query, categories);
  const shown = applyQuery(list.gifts, query);
  renderChips(categories);
  syncSelects();
  $('toolbar').hidden = list.gifts.length === 0;
  renderSection('pots', shown.filter((g) => g.kind === 'potje'));
  renderSection('gifts', shown.filter((g) => g.kind !== 'potje'));
  renderEmpty(list.gifts.length, shown.length);
  if (fk) document.querySelector<HTMLElement>(`[data-fk="${CSS.escape(fk)}"]`)?.focus();
}

export function renderListError(): void {
  $('toolbar').hidden = true;
  $('potsSection').hidden = true;
  $('giftsSection').hidden = true;
  $('emptyState').hidden = false;
  $('emptyText').textContent = 'De lijst kan nu niet geladen worden. Probeer het straks opnieuw.';
  $('showAll').hidden = true;
}

export function initListControls(onChange: () => void): void {
  onQueryChange = onChange;
  const toon = $('toon') as HTMLSelectElement;
  const sort = $('sort') as HTMLSelectElement;
  toon.addEventListener('change', () => setQuery({ ...query, show: parseListQuery(new URLSearchParams({ toon: toon.value })).show }));
  sort.addEventListener('change', () => setQuery({ ...query, sort: parseListQuery(new URLSearchParams({ sort: sort.value })).sort }));
  $('showAll').addEventListener('click', () => setQuery(DEFAULT_QUERY));
}
